import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './ConfirmTrip.css';
import CustomCalendar from './CustomCalendar';
import CustomTimePicker from './CustomTimePicker';
import { useLanguage } from './i18n/LanguageContext';

// Fix for default marker icons in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const ConfirmTrip = ({ selectedLocation, onBack, onSelectVehicle }) => {
  const { t } = useLanguage();
  const [guests, setGuests] = useState(2);
  const [bags, setBags] = useState(1);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [timeError, setTimeError] = useState(null);
  const [clock, setClock] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setClock(Date.now()), 15000);
    return () => clearInterval(id);
  }, []);

  const startOfMinute = (date) => {
    const copy = new Date(date);
    copy.setSeconds(0, 0);
    return copy;
  };

  const parseLocalDateTime = (date, time) => {
    const [year, month, day] = String(date).split('-').map(Number);
    const [hours, minutes] = String(time).split(':').map(Number);
    return new Date(year, (month || 1) - 1, day || 1, hours || 0, minutes || 0, 0, 0);
  };

  const minimumBookingTime = (now = new Date()) => {
    const min = startOfMinute(now);
    min.setMinutes(min.getMinutes() + 30);
    return min;
  };

  const formatClockTime = (date) => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Calculate default time as current time + 30 minutes, on the minute
  const getDefaultTime = () => formatClockTime(minimumBookingTime());

  // Initialize date and time
  const today = new Date();
  const [selectedDate, setSelectedDate] = useState(today.toISOString().split('T')[0]);
  const [selectedTime, setSelectedTime] = useState(getDefaultTime());

  // Format date for display (e.g., "13 Jan")
  const formatDate = (dateString) => {
    const date = new Date(`${dateString}T00:00:00`);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  // Format time for display
  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Get hotel information from localStorage
  const selectedHotel = JSON.parse(localStorage.getItem('selectedHotel') || '{}');
  const pickupPoint = selectedHotel.hotelName || 'Hotel Radisson Blu';
  const dropDestination = selectedLocation?.locationName || 'Indira Gandhi International';

  // Get coordinates for map
  const pickupLat = selectedHotel.latitude || 28.5451;
  const pickupLng = selectedHotel.longitude || 77.3297;
  const dropLat = selectedLocation?.latitude || 28.5565;
  const dropLng = selectedLocation?.longitude || 77.1000;

  const handleGuestChange = (delta) => {
    const newValue = guests + delta;
    if (newValue >= 1 && newValue <= 10) {
      setGuests(newValue);
    }
  };

  const handleBagChange = (delta) => {
    const newValue = bags + delta;
    if (newValue >= 0 && newValue <= 10) {
      setBags(newValue);
    }
  };

  // Validate if selected date/time is at least 30 minutes from now.
  // Compare whole minutes so 4:40:45 + 30 minutes (shown as 5:10) counts as valid.
  const validateDateTime = (date, time, now = new Date(clock)) => {
    const selectedDateTime = parseLocalDateTime(date, time);
    return selectedDateTime >= minimumBookingTime(now);
  };

  // Get minimum time for time input based on selected date
  const getMinTime = () => {
    const selectedDateObj = parseLocalDateTime(selectedDate, '00:00');
    const today = startOfMinute(new Date(clock));
    today.setHours(0, 0, 0, 0);

    // If selected date is today, min time is the current minute + 30 minutes
    if (selectedDateObj.getTime() === today.getTime()) {
      return formatClockTime(minimumBookingTime(new Date(clock)));
    }
    // Future dates can start at midnight
    return '00:00';
  };

  // Check if selected time is in the past
  const isTimeInPast = (date, time, now = new Date(clock)) => {
    const selectedDateTime = parseLocalDateTime(date, time);
    return selectedDateTime < startOfMinute(now);
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    setShowDatePicker(false);
    setTimeError(null);

    // If date changed to today, adjust time if needed
    const now = new Date();
    const selectedDateObj = new Date(newDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDateObj.setHours(0, 0, 0, 0);

    if (selectedDateObj.getTime() === today.getTime()) {
      // If date is today, ensure time is at least 30 minutes from now
      if (!validateDateTime(newDate, selectedTime)) {
        const defaultTime = getDefaultTime();
        setSelectedTime(defaultTime);
      }
    }
  };

  const handleTimeChange = (e) => {
    const newTime = e.target.value;

    // Check if time is in the past
    if (isTimeInPast(selectedDate, newTime)) {
      setTimeError('Cannot select a time in the past');
      return;
    }

    setSelectedTime(newTime);
    setShowTimePicker(false);

    // Validate the new time (must be at least 30 minutes from now)
    if (!validateDateTime(selectedDate, newTime)) {
      setTimeError('Booking must be at least 30 minutes from now');
    } else {
      setTimeError(null);
    }
  };

  const handleSelectVehicle = () => {
    // First check if time is in the past
    if (isTimeInPast(selectedDate, selectedTime)) {
      setTimeError('Cannot select a time in the past');
      return;
    }

    // Validate date/time before proceeding (must be at least 30 minutes from now)
    if (!validateDateTime(selectedDate, selectedTime)) {
      setTimeError('Booking must be at least 30 minutes from now');
      return;
    }

    setTimeError(null);

    const tripData = {
      pickupPoint,
      dropDestination,
      guests,
      bags,
      date: selectedDate,
      time: selectedTime,
      formattedDate: formatDate(selectedDate),
      formattedTime: formatTime(selectedTime),
      pickupLocation: {
        latitude: pickupLat,
        longitude: pickupLng
      },
      dropLocation: {
        latitude: dropLat,
        longitude: dropLng
      },
      locationId: selectedLocation?.id,
      categoryId: null // You can add categoryId if available
    };
    onSelectVehicle(tripData);
  };

  // Check if booking time is valid (must be in the future and at least 30 minutes from now)
  const isBookingTimeValid = () => {
    // First check if time is in the past
    if (isTimeInPast(selectedDate, selectedTime)) {
      return false;
    }
    // Then check if it's at least 30 minutes from now
    return validateDateTime(selectedDate, selectedTime);
  };

  // Validate time on mount and when date/time changes
  useEffect(() => {
    // First check if time is in the past
    if (isTimeInPast(selectedDate, selectedTime)) {
      setTimeError('Cannot select a time in the past');
    } else if (!validateDateTime(selectedDate, selectedTime)) {
      setTimeError('Booking must be at least 30 minutes from now');
    } else {
      setTimeError(null);
    }
  }, [selectedDate, selectedTime, clock]);

  // Close calendar/time picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDatePicker && !event.target.closest('.calendar-popup') && !event.target.closest('.parameter-field')) {
        setShowDatePicker(false);
      }
      if (showTimePicker && !event.target.closest('.time-picker-popup') && !event.target.closest('.parameter-field')) {
        setShowTimePicker(false);
      }
    };

    if (showDatePicker || showTimePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker, showTimePicker]);

  // Create custom icons for markers using divIcon
  const pickupIcon = L.divIcon({
    className: 'custom-marker pickup-marker-icon',
    html: '<div style="background-color: #4CAF50; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);"></div>',
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });

  const dropIcon = L.divIcon({
    className: 'custom-marker drop-marker-icon',
    html: '<div style="background-color: #FF9800; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);"></div>',
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });

  // Calculate center point between pickup and drop
  const centerLat = (pickupLat + dropLat) / 2;
  const centerLng = (pickupLng + dropLng) / 2;

  // Route coordinates
  const routeCoordinates = [
    [pickupLat, pickupLng],
    [dropLat, dropLng]
  ];

  return (
    <div className="confirm-trip-screen">
      {/* Top Navigation */}
      <header className="trip-header">
        <button className="back-button" onClick={onBack} aria-label="Go back">
          <img
            src={process.env.PUBLIC_URL + '/images/back_button.svg'}
            alt="Back"
          />
        </button>
        <h1 className="trip-title">{t('Confirm Trip')}</h1>
        <div style={{ width: '40px' }}></div> {/* Spacer for centering */}
      </header>

      {/* Map Area with Route Indicator */}
      <div className="map-container">
        <div className="route-indicator">
          <div className="route-line"></div>
          <div className="route-circle pickup-circle"></div>
        </div>
        {centerLat && centerLng && (
          <MapContainer
            center={[centerLat, centerLng]}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={false}
            key={`${centerLat}-${centerLng}`}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={[pickupLat, pickupLng]} icon={pickupIcon}>
              <Popup>{pickupPoint}</Popup>
            </Marker>
            <Marker position={[dropLat, dropLng]} icon={dropIcon}>
              <Popup>{dropDestination}</Popup>
            </Marker>
            <Polyline
              positions={routeCoordinates}
              pathOptions={{
                color: '#764CA2',
                weight: 4,
                opacity: 0.7
              }}
            />
          </MapContainer>
        )}
      </div>

      {/* Pickup and Drop-off Card */}
      <div className="maping-in-map">

        <div className="pickup-point">
          <div className="trip-sheet">
          <div className="location-card">
            <div className="location-item">
              <div className="location-dot pickup-dot"></div>
              <div className="location-details">
                <span className="location-label">{t('PICKUP POINT')}</span>
                <span className="location-name">{pickupPoint}</span>
              </div>
            </div>
            <div className="location-item">
              <div className="location-dot drop-dot"></div>
              <div className="location-details">
                <span className="location-label drop-label">
                  <span className="drop-label-mobile">{t('DROP DESTINATION')}</span>
                  <span className="drop-label-desktop">{t('DROP OFF')}</span>
                </span>
                <span className="location-name">{dropDestination}</span>
              </div>
            </div>
          </div>

          {/* Trip Parameters */}
          <div className="trip-parameters">
            <div className="parameter-row">
              <div className="parameter-field">
                <span className="parameter-label">{t('GUESTS')}</span>
                <div className="parameter-control">
                  <svg className="parameter-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H11C9.93913 15 8.92172 15.4214 8.17157 16.1716C7.42143 16.9217 7 17.9391 7 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  <span className="parameter-value">{guests}</span>
                  <div className="parameter-buttons">
                    <button className="param-btn" onClick={() => handleGuestChange(-1)} disabled={guests <= 1}>
                      −
                    </button>
                    <button className="param-btn" onClick={() => handleGuestChange(1)} disabled={guests >= 10}>
                      +
                    </button>
                  </div>
                </div>
              </div>

              <div className="parameter-field">
                <span className="parameter-label">{t('BAGS')}</span>
                <div className="parameter-control">
                  <svg className="parameter-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 2L3 6V20C3 20.5304 3.21071 21.0391 3.58579 21.4142C3.96086 21.7893 4.46957 22 5 22H19C19.5304 22 20.0391 21.7893 20.4142 21.4142C20.7893 21.0391 21 20.5304 21 20V6L18 2H6Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M3 6H21" stroke="currentColor" strokeWidth="2" />
                    <path d="M16 10C16 11.1046 15.1046 12 14 12C12.8954 12 12 11.1046 12 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  <span className="parameter-value">{bags}</span>
                  <div className="parameter-buttons">
                    <button className="param-btn" onClick={() => handleBagChange(-1)} disabled={bags <= 0}>
                      −
                    </button>
                    <button className="param-btn" onClick={() => handleBagChange(1)} disabled={bags >= 10}>
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="parameter-row">
              <div className="parameter-field">
                <span className="parameter-label">{t('DATE')}</span>
                <div className="parameter-control">
                  <svg className="parameter-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                    <path d="M16 2V6M8 2V6M3 10H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  <span className="parameter-value">{formatDate(selectedDate)}</span>
                  <button
                    className="dropdown-btn"
                    onClick={() => {
                      setShowDatePicker(!showDatePicker);
                      setShowTimePicker(false);
                    }}
                    aria-label="Select date"
                  >
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
                {showDatePicker && createPortal(
                  <>
                    <div className="popup-backdrop" onClick={() => setShowDatePicker(false)}></div>
                    <div className="calendar-popup">
                      <CustomCalendar
                        selectedDate={selectedDate}
                        onDateSelect={(date) => {
                          handleDateChange({ target: { value: date } });
                          setShowDatePicker(false);
                        }}
                        onClose={() => setShowDatePicker(false)}
                        minDate={today.toISOString().split('T')[0]}
                      />
                    </div>
                  </>,
                  document.body
                )}
              </div>

              <div className="parameter-field">
                <span className="parameter-label">{t('TIME')}</span>
                <div className="parameter-control">
                  <svg className="parameter-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                    <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  <span className="parameter-value">{formatTime(selectedTime)}</span>
                  <button
                    className="dropdown-btn"
                    onClick={() => {
                      setShowTimePicker(!showTimePicker);
                      setShowDatePicker(false);
                    }}
                    aria-label="Select time"
                  >
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
                {showTimePicker && createPortal(
                  <>
                    <div className="popup-backdrop" onClick={() => setShowTimePicker(false)}></div>
                    <div className="time-picker-popup">
                      <CustomTimePicker
                        selectedTime={selectedTime}
                        onTimeSelect={(time) => {
                          handleTimeChange({ target: { value: time } });
                          setShowTimePicker(false);
                        }}
                        onCancel={() => setShowTimePicker(false)}
                        minTime={getMinTime()}
                      />
                    </div>
                  </>,
                  document.body
                )}
              </div>
            </div>
          </div>
          </div>
        </div>
        {/* Time Error Message */}
        {timeError && (
          <div className="time-error-message">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
              <path d="M12 8V12M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span>{timeError}</span>
          </div>
        )}

        {/* Select Vehicle Button */}
        <button
          className={`select-vehicle-btn ${!isBookingTimeValid() ? 'disabled' : ''}`}
          onClick={handleSelectVehicle}
          disabled={!isBookingTimeValid()}
        >
          Select Vehicle
        </button>
      </div>
    </div>
  );
};

export default ConfirmTrip;
